import { useState } from "react";
import { Button, EmptyState } from "@astrum/ui";
import { formatErrorDetails } from "../lib/error-report.js";

/**
 * Shown by the outermost error boundary instead of a white page.
 *
 * Deliberately self-contained: it renders from outside every provider, so it
 * cannot rely on toasts, auth, or routing being alive.
 */
export function CrashScreen({ error }: { error: Error }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    const text = `${formatErrorDetails(error.message)}\nLocation: ${window.location.pathname}\n\n${error.stack ?? ""}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // Clipboard blocked; nothing more we can do from a crashed tree.
    }
  };

  return (
    <EmptyState
      icon="warning"
      title="Something went wrong"
      description="The app hit an unexpected error. Reloading usually fixes it. If it keeps happening, copy the details and send them to your administrator."
      action={
        <div style={{ display: "flex", gap: "var(--sp-2)" }}>
          <Button variant="primary" onClick={() => window.location.reload()}>
            Reload
          </Button>
          <Button variant="secondary" onClick={copy}>
            {copied ? "Copied" : "Copy details"}
          </Button>
        </div>
      }
    />
  );
}
